/**
 * useAnimationFrame - rAF 帧调度 Hook
 *
 * Drives the SM-1 playback loop via requestAnimationFrame. When `isPlaying`
 * is true, a rAF loop is started; each tick computes the wall-clock delta
 * scaled by `speedMultiplier` and forwards it to `onFrame`.
 *
 * 通过 requestAnimationFrame 驱动 SM-1 回放循环。当 `isPlaying` 为 true 时
 * 启动 rAF 循环；每次回调计算墙钟增量并乘以倍速后转发给 `onFrame`。
 *
 * Behavior / 行为:
 *   - When `isPlaying` becomes true, the rAF loop starts.
 *   - Each tick: wallDelta = (now - lastNow) * speedMultiplier.
 *     Call `onFrame(wallDelta)`.
 *   - On `visibilitychange` (document.hidden): cancel the active rAF and
 *     reset `lastNow` so the next visible frame doesn't produce a huge jump.
 *     When the tab becomes visible again, the loop resumes if still playing.
 *   - The caller is responsible for end-of-playback detection: when the
 *     accumulated playback timestamp reaches `maxTimestamp`, the caller
 *     should transition out of the playing state (which sets `isPlaying`
 *     to false), causing this hook's effect cleanup to cancel the rAF.
 *     `onEnd` is invoked by the caller's own end-check path.
 *
 * 为什么 useAnimationFrame 不在内部检测结束 / Why useAnimationFrame does not
 * track absolute time internally:
 *   The source of truth for the playback timestamp lives in the SM-1 reducer
 *   (PlaybackContext.currentTimestamp). Seeks, replays, and pauses mutate it
 *   in ways this hook cannot observe. Performing the end check in the caller's
 *   `onFrame` callback keeps a single source of truth and avoids divergence
 *   between the hook's internal accumulation and the reducer's timestamp.
 *
 * 回放时间戳的唯一真源在 SM-1 reducer 中。seek、重播、暂停会以本 hook
 * 无法观察的方式修改它。在调用方 `onFrame` 中检测结束可保持单一真源，
 * 避免 hook 内部累加值与 reducer 时间戳之间的偏差。
 */

import { useEffect, useRef } from 'react';

/** Options for {@link useAnimationFrame}. */
export interface UseAnimationFrameOptions {
  /** Whether the rAF loop should be running. / 是否正在播放（驱动 rAF 循环）。 */
  isPlaying: boolean;
  /** Wall-clock multiplier applied to each frame delta. / 墙钟增量倍速。 */
  speedMultiplier: number;
  /**
   * Called every animation frame with the scaled wall-clock delta (ms).
   * / 每帧回调，参数为经倍速缩放后的墙钟增量（毫秒）。
   */
  onFrame: (deltaMs: number) => void;
  /**
   * Called when playback should end (timestamp reached maxTimestamp).
   * Invoked by the caller's end-check logic; kept in the API for spec
   * completeness and as a hook for callers that manage their own timing.
   *
   * 当回放应结束时调用（时间戳到达 maxTimestamp）。由调用方的结束检测
   * 逻辑触发；保留在 API 中以符合规格并为自管理时序的调用方提供钩子。
   */
  onEnd?: () => void;
  /** Maximum playback timestamp (ms). Used by the caller for end detection. */
  maxTimestamp: number;
}

/**
 * rAF-based frame scheduling hook.
 *
 * @param options - see {@link UseAnimationFrameOptions}
 */
export function useAnimationFrame(options: UseAnimationFrameOptions): void {
  const { isPlaying, speedMultiplier, onFrame, onEnd } = options;
  // Note: `maxTimestamp` is accepted in the options (per spec) but end-of-
  // playback detection is performed by the caller in `onFrame`, where the
  // authoritative timestamp from the SM-1 reducer is available. Keeping the
  // check in the caller avoids divergence between this hook's internal
  // accumulation and the reducer's currentTimestamp (see file header).
  // 注：`maxTimestamp` 在 options 中接受（符合规格），但回放结束检测由调用方
  // 在 `onFrame` 中执行，此时可读取 SM-1 reducer 的权威时间戳。在调用方检测
  // 可避免本 hook 内部累加值与 reducer currentTimestamp 之间的偏差。

  // Refs hold the latest option values so the rAF effect (which only depends
  // on `isPlaying`) always reads current data without restarting the loop.
  // ref 持有最新选项值，使仅依赖 `isPlaying` 的 rAF effect 无需重启即可读取最新值。
  const onFrameRef = useRef(onFrame);
  const onEndRef = useRef(onEnd);
  const speedRef = useRef(speedMultiplier);

  // Update refs synchronously during render so the next rAF tick sees the
  // latest callbacks and values. This is the standard "latest value ref"
  // pattern and avoids stale closures inside the rAF loop.
  // 在渲染期间同步更新 ref，确保下一次 rAF 回调读到最新值，避免闭包过期。
  onFrameRef.current = onFrame;
  onEndRef.current = onEnd;
  speedRef.current = speedMultiplier;

  useEffect(() => {
    if (!isPlaying) return;

    let rafId: number | null = null;
    let lastNow: number | null = null;
    let stopped = false;

    const tick = (now: number) => {
      if (stopped) return;

      // First frame after start/resume: record timestamp, don't advance.
      // This prevents a large delta jump after pauses or tab switches.
      // 启动/恢复后的首帧：仅记录时间戳，不推进，避免暂停或切标签后的大跳变。
      if (lastNow == null) {
        lastNow = now;
        rafId = requestAnimationFrame(tick);
        return;
      }

      const wallDelta = (now - lastNow) * speedRef.current;
      lastNow = now;

      // Forward the scaled delta to the caller. The caller updates its own
      // state (e.g. dispatches FRAME_UPDATE) and performs end-of-playback
      // detection based on its authoritative timestamp.
      // 将缩放后的增量转发给调用方。调用方更新自身状态并基于权威时间戳检测结束。
      onFrameRef.current(wallDelta);

      rafId = requestAnimationFrame(tick);
    };

    const start = () => {
      if (rafId == null && !stopped) {
        lastNow = null;
        rafId = requestAnimationFrame(tick);
      }
    };

    const stop = () => {
      if (rafId != null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
      lastNow = null;
    };

    // visibilitychange: cancel rAF immediately when the tab is hidden to
    // halt playback. When the tab becomes visible again, resume the loop
    // only if the effect is still active (i.e. still playing). The SM-1
    // reducer separately dispatches TAB_HIDDEN to transition out of the
    // Playing state, which will trigger this effect's cleanup.
    //
    // 标签页隐藏时立即取消 rAF 以暂停推进；标签页恢复时若仍在播放则恢复循环。
    // SM-1 reducer 会另行派发 TAB_HIDDEN 以退出 Playing 状态，触发本 effect 清理。
    const handleVisibility = () => {
      if (document.hidden) {
        stop();
      } else {
        start();
      }
    };

    start();
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      stopped = true;
      stop();
      document.removeEventListener('visibilitychange', handleVisibility);
    };
    // Only re-run when play/pause toggles. Callback/speed changes are picked
    // up via refs. 仅在播放/暂停切换时重启；回调和倍速变化通过 ref 读取。
  }, [isPlaying]);
}
